/* eslint-disable array-bracket-spacing */
import { invariant } from '../util/invariant';
import type { FieldOrder, PixelFormat, Resolution } from './types';
import type { DNxHRProfile, H264Profile } from './codecs';
import { FRAC_ONE, type Fraction } from '../util/fractionType';
import type { FilterableOutputProfile } from './filterableOutputProfile';
import { frac } from '../util/integerMath';
import { safeArrayAccess } from '../util/safeArrayAccess';
import type { ColorSpace } from '../types/colorSpace';


export function generateAllBroadcastProfiles(): FilterableOutputProfile[] {
  if (CACHED_ALL_BROADCAST_PROFILES === null) {
    CACHED_ALL_BROADCAST_PROFILES = [
      ...generateXdcamProfiles(),
      ...generateAvcIntraProfiles(),
      ...generateXavcIntraProfiles(),
      ...generateDnxhdProfiles(),
      ...generateDnxhrProfiles(),
    ];
  }
  return CACHED_ALL_BROADCAST_PROFILES;
}


export function generateWorkingBroadcastProfiles(): FilterableOutputProfile[] {
  if (CACHED_WORKING_BROADCAST_PROFILES === null) {
    const working: FilterableOutputProfile[] = [];
    for (const profile of generateAllBroadcastProfiles()) {
      // All are currently working :) Leaving the function here for now so that we have
      // a fixed place to filter out broken profiles.
      working.push(profile);
    }
    CACHED_WORKING_BROADCAST_PROFILES = working;
  }
  return CACHED_WORKING_BROADCAST_PROFILES;
}

// End of public API
let CACHED_ALL_BROADCAST_PROFILES : FilterableOutputProfile[] | null = null;
let CACHED_WORKING_BROADCAST_PROFILES : FilterableOutputProfile[] | null = null;

function generateXdcamProfiles(): FilterableOutputProfile[] {

  interface VideoMode {
    fieldRate: Fraction,
    fieldOrder: FieldOrder,
  }
  interface TableRow {
    resolution: Resolution,
    modes: VideoMode[],
  }

  /* This structure represents the MPEG HD422 row in https://en.wikipedia.org/wiki/XDCAM */
  const rows : TableRow[] = [
    {
      resolution: {
        width: 1920,
        height: 1080,
      },
      modes: [
        { fieldRate: { numerator: 60000, denominator: 1001 }, fieldOrder: 'top_field_first' },
        { fieldRate: { numerator: 50, denominator: 1 }, fieldOrder: 'top_field_first' },
        { fieldRate: { numerator: 30000, denominator: 1001 }, fieldOrder: 'progressive' },
        { fieldRate: { numerator: 25, denominator: 1 }, fieldOrder: 'progressive' },
        { fieldRate: { numerator: 24000, denominator: 1001 }, fieldOrder: 'progressive' },
      ],
    },
    {
      resolution: {
        width: 1280,
        height: 720,
      },
      modes: [
        { fieldRate: { numerator: 60000, denominator: 1001 }, fieldOrder: 'progressive' },
        { fieldRate: { numerator: 50, denominator: 1 }, fieldOrder: 'progressive' },
        { fieldRate: { numerator: 24000, denominator: 1001 }, fieldOrder: 'progressive' },
      ],
    }
  ];

  const audioChannelCounts = [4, 8, 16];

  const profiles : FilterableOutputProfile[] = [];
  for (const row of rows) {
    for (const mode of row.modes) {
      for (const audioChannelCount of audioChannelCounts) {
        const fieldsPerFrame = mode.fieldOrder === 'progressive' ? 1 : 2;
        const outputProfile : FilterableOutputProfile = {
          type: 'broadcast',
          brandName: 'XDCAM',
          variant: 'HD422',
          container: {
            type: 'single_file',
            format: { name: 'mxf' },
          },
          video: {
            codec: {
              codec: 'mpeg2',
              rateControl: {
                type: 'cbr',
                bitrate: 50000000,
              },
              gopSize: 12,
              bFrames: 2,
              closedGop: true,
            },
            pixelFormat: 'yuv422p',
            resolution: row.resolution,
            frameRate: frac(mode.fieldRate.numerator / fieldsPerFrame, mode.fieldRate.denominator),
            fieldOrder: mode.fieldOrder,
            colorSpace: {
              matrix: {
                type: 'BT.709',
                range: 'limited',
              },
              primaries: 'BT.709',
              transfer: 'BT.709',
            },
          },
          audio: {
            codec: {
              codec: 'pcm',
              bitDepth: 24,
            },
            sampleRate: 48000,
            channelLimits: {
              min: audioChannelCount,
              max: audioChannelCount,
              defaultMix: false,
            },
            audioChannelsPerStream: 1,
          }
        };

        profiles.push(outputProfile);
      }
    }
  }

  return profiles;
}

function generateAvcIntraProfiles(): FilterableOutputProfile[] {
  interface TableRow {
    resolution: Resolution,
    pixelFormat: PixelFormat,
    class: 50 | 100 | 200,
    bitrates: number[],
  }

  interface VideoMode {
    fieldRate: Fraction,
    fieldOrder: FieldOrder,
  }

  const NOT : number = 0;

  const rows : TableRow[] = [
      // HD Flavors
      //     Resolution                    Chroma sampling / Bitdepth   Classs                         50i  59i  23p  24p  25p  29p  50p  59p
      { resolution: { width: 1280, height: 720 }, pixelFormat: 'yuv422p10le', class: 100, bitrates:  [NOT, NOT, NOT, NOT, NOT, NOT, NOT, 111] },
      { resolution: { width: 1280, height: 720 }, pixelFormat: 'yuv422p10le', class: 100, bitrates:  [NOT, NOT, NOT, NOT, NOT, NOT, 112, NOT] },
      { resolution: { width: 1440, height: 1080 }, pixelFormat: 'yuv420p10le', class: 50, bitrates:  [NOT,  54,  54, NOT, NOT,  54, NOT, NOT] },
      { resolution: { width: 1440, height: 1080 }, pixelFormat: 'yuv420p10le', class: 50, bitrates:   [43, NOT, NOT, NOT,  54, NOT, NOT, NOT] },
      { resolution: { width: 1920, height: 1080 }, pixelFormat: 'yuv422p10le', class: 100, bitrates: [NOT, 110,  89, NOT, NOT, 111, NOT, 222] },
      { resolution: { width: 1920, height: 1080 }, pixelFormat: 'yuv422p10le', class: 100, bitrates: [112, NOT, NOT, NOT, 112, NOT, 223, NOT] },
      { resolution: { width: 1920, height: 1080 }, pixelFormat: 'yuv422p10le', class: 200, bitrates: [NOT, 226, 181, NOT, NOT, 226, NOT, 452] },
      { resolution: { width: 1920, height: 1080 }, pixelFormat: 'yuv422p10le', class: 200, bitrates: [227, NOT, NOT, NOT, 227, NOT, 454, NOT] },
  ];

  // This list defines the frame rates for the last column in the previous table
  const modes : VideoMode[] = [
    {
      fieldRate: {
        numerator: 50,
        denominator: 1,
      },
      fieldOrder: 'top_field_first',
    },
    {
      fieldRate: {
        numerator: 60000,
        denominator: 1001,
      },
      fieldOrder: 'top_field_first',
    },
    {
      fieldRate: {
        numerator: 24000,
        denominator: 1001,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 24,
        denominator: 1,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 25,
        denominator: 1,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 30000,
        denominator: 1001,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 50,
        denominator: 1,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 60000,
        denominator: 1001,
      },
      fieldOrder: 'progressive',
    },
  ];

  const colorSpaces : ColorSpace[] = [
    {
      matrix: {
        type: 'BT.709',
        range: 'limited',
      },
      primaries: 'BT.709',
      transfer: 'BT.709',
    },
    {
      matrix: {
        type: 'BT.2020',
        range: 'limited',
      },
      primaries: 'BT.2020',
      transfer: 'HLG',
    },
  ];

  const audioChannelCounts = [2, 4, 8, 16];

  const profiles : FilterableOutputProfile[] = [];
  for (const row of rows) {
    for (let modeIndex = 0; modeIndex < row.bitrates.length; modeIndex++) {
      const videoMode = safeArrayAccess(modes, modeIndex);
      const bitrate = safeArrayAccess(row.bitrates, modeIndex);
      if (bitrate === NOT) {
        continue;
      }
      const sar = (row.resolution.width === 1440) ? { numerator: 4, denominator: 3 } : undefined;
      const fieldsPerFrame = videoMode.fieldOrder === 'progressive' ? 1 : 2;
      const profile : H264Profile = row.pixelFormat === 'yuv422p10le' ? 'high422' : 'high10';
      for (const audioChannelCount of audioChannelCounts) {
        for (const colorSpace of colorSpaces) {
          const outputProfile : FilterableOutputProfile = {
            type: 'broadcast',
            brandName: 'AVC-Intra',
            variant: `Class ${row.class}`,
            container: {
              type: 'single_file',
              format: { name: 'mxf' },
            },
            video: {
              codec: {
                codec: 'h264',
                rateControl: {
                  type: 'cbr',
                  bitrate: bitrate * 1000000,
                },
                gopSize: 1,
                bFrames: 0,
                profile,
                preset: 'veryfast',
                avc: {
                  flavor: 'panasonic',
                  class: row.class,
                },
              },
              fieldOrder: videoMode.fieldOrder,
              resolution: {
                ...row.resolution,
                sar,
              },
              frameRate: frac(videoMode.fieldRate.numerator / fieldsPerFrame, videoMode.fieldRate.denominator),
              pixelFormat: row.pixelFormat,
              colorSpace,
            },
            audio: {
              codec: {
                codec: 'pcm',
                bitDepth: 24,
              },
              sampleRate: 48000,
              audioChannelsPerStream: 1,
              channelLimits: {
                min: audioChannelCount,
                max: audioChannelCount,
                defaultMix: false,
              },
            }
          };

          profiles.push(outputProfile);
        }
      }
    }
  }

  return profiles;
}

function generateXavcIntraProfiles(): FilterableOutputProfile[] {
  interface TableRow {
    resolution: Resolution,
    pixelFormat: PixelFormat,
    class: 50 | 100 | 200 | 300 | 480,
    bitrates: number[],
  }

  interface VideoMode {
    fieldRate: Fraction,
    fieldOrder: FieldOrder,
  }

  const NOT : number = 0;

  const rows : TableRow[] = [
      // HD Flavors
      //     Resolution                    Chroma sampling / Bitdepth   Classs                         50i  59i  23p  24p  25p  29p  50p  59p
      { resolution: { width: 1280, height: 720 }, pixelFormat: 'yuv422p10le', class: 100, bitrates:  [NOT, NOT, NOT, NOT, NOT, NOT, NOT, 111] },
      { resolution: { width: 1280, height: 720 }, pixelFormat: 'yuv422p10le', class: 100, bitrates:  [NOT, NOT, NOT, NOT, NOT, NOT, 112, NOT] },
      { resolution: { width: 1440, height: 1080 }, pixelFormat: 'yuv420p10le', class: 50, bitrates:  [NOT,  54,  54, NOT, NOT,  54, NOT, NOT] },
      { resolution: { width: 1440, height: 1080 }, pixelFormat: 'yuv420p10le', class: 50, bitrates:   [43, NOT, NOT, NOT,  54, NOT, NOT, NOT] },
      { resolution: { width: 1920, height: 1080 }, pixelFormat: 'yuv422p10le', class: 100, bitrates: [NOT, 110,  89, NOT, NOT, 111, NOT, 222] },
      { resolution: { width: 1920, height: 1080 }, pixelFormat: 'yuv422p10le', class: 100, bitrates: [112, NOT, NOT, NOT, 112, NOT, 223, NOT] },
      { resolution: { width: 1920, height: 1080 }, pixelFormat: 'yuv422p10le', class: 200, bitrates: [NOT, 226, 181, NOT, NOT, 226, NOT, 452] },
      { resolution: { width: 1920, height: 1080 }, pixelFormat: 'yuv422p10le', class: 200, bitrates: [227, NOT, NOT, NOT, 227, NOT, 454, NOT] },
      // 4k Flavors (not all of these are 4K, but they are all in the "4k" category in the XAVC standards)
      { resolution: { width: 2048, height: 1080 }, pixelFormat: 'yuv422p10le', class: 100, bitrates: [NOT, NOT,  89, NOT,  93, 111, 185, 222] },
      { resolution: { width: 3840, height: 2160 }, pixelFormat: 'yuv422p10le', class: 300, bitrates: [NOT, NOT, 240, NOT, 250, 300, 500, 600] },
      { resolution: { width: 3840, height: 2160 }, pixelFormat: 'yuv422p10le', class: 480, bitrates: [NOT, NOT, 384, NOT, 400, 300, 800, 960] },
      { resolution: { width: 4096, height: 2160 }, pixelFormat: 'yuv422p10le', class: 300, bitrates: [NOT, NOT, 240, NOT, 250, 300, 500, 600] },
      { resolution: { width: 4096, height: 2160 }, pixelFormat: 'yuv422p10le', class: 480, bitrates: [NOT, NOT, 384, NOT, 400, 300, 800, 960] },
  ];

  // This list defines the frame rates for the last column in the previous table
  const modes : VideoMode[] = [
    {
      fieldRate: {
        numerator: 50,
        denominator: 1,
      },
      fieldOrder: 'top_field_first',
    },
    {
      fieldRate: {
        numerator: 60000,
        denominator: 1001,
      },
      fieldOrder: 'top_field_first',
    },
    {
      fieldRate: {
        numerator: 24000,
        denominator: 1001,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 24,
        denominator: 1,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 25,
        denominator: 1,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 30000,
        denominator: 1001,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 50,
        denominator: 1,
      },
      fieldOrder: 'progressive',
    },
    {
      fieldRate: {
        numerator: 60000,
        denominator: 1001,
      },
      fieldOrder: 'progressive',
    },
  ];

  const colorSpaces : ColorSpace[] = [
    {
      matrix: {
        type: 'BT.709',
        range: 'limited',
      },
      primaries: 'BT.709',
      transfer: 'BT.709',
    },
    {
      matrix: {
        type: 'BT.2020',
        range: 'limited',
      },
      primaries: 'BT.2020',
      transfer: 'HLG',
    },
  ];

  const audioChannelCounts = [2, 4, 8, 16];

  const profiles : FilterableOutputProfile[] = [];
  for (const row of rows) {
    for (let modeIndex = 0; modeIndex < row.bitrates.length; modeIndex++) {
      const videoMode = safeArrayAccess(modes, modeIndex);
      const bitrate = safeArrayAccess(row.bitrates, modeIndex);
      if (bitrate === NOT) {
        continue;
      }
      const sar = (row.resolution.width === 1440) ? { numerator: 4, denominator: 3 } : undefined;
      const fieldsPerFrame = videoMode?.fieldOrder === 'progressive' ? 1 : 2;
      const profile : H264Profile = row.pixelFormat === 'yuv422p10le' ? 'high422' : 'high10';
      for (const audioChannelCount of audioChannelCounts) {
        for (const colorSpace of colorSpaces) {
          const outputProfile : FilterableOutputProfile = {
            type: 'broadcast',
            brandName: 'XAVC-Intra',
            variant: `Class ${row.class}`,
            container: {
              type: 'single_file',
              format: { name: 'mxf' },
            },
            video: {
              codec: {
                codec: 'h264',
                rateControl: {
                  type: 'cbr',
                  bitrate: bitrate * 1000000,
                },
                gopSize: 1,
                bFrames: 0,
                profile,
                preset: 'veryfast',
                avc: {
                  flavor: 'sony',
                  class: row.class,
                }
              },
              resolution: {
                ...row.resolution,
                sar,
              },
              frameRate: frac(videoMode.fieldRate.numerator / fieldsPerFrame, videoMode.fieldRate.denominator),
              pixelFormat: row.pixelFormat,
              fieldOrder: videoMode.fieldOrder,
              colorSpace,
            },
            audio: {
              codec: {
                codec: 'pcm',
                bitDepth: 24,
              },
              sampleRate: 48000,
              audioChannelsPerStream: 1,
              channelLimits: {
                min: audioChannelCount,
                max: audioChannelCount,
                defaultMix: false,
              },
            }
          };

          profiles.push(outputProfile);
        }
      }
    }
  }

  return profiles;
}

function generateDnxhrProfiles(): FilterableOutputProfile[] {

  // Source: https://resources.avid.com/SupportFiles/attach/HighRes_WorkflowsGuide.pdf (Chapter 6: DNxHR Family)
  // Note that ffmpeg only needs the profile name in order to set up DNxHR encoding. So these bitrate values
  // are actually only used to identify the not applicable combinations (NA)
  const NA = -1;
  const dnxhrTable = [
    {
      resolution: '2K',
      frameRates: [ //            1/16 LB   1/4 LB    LB    SQ    HQ    HQX   444
        { frameRate: '24p', bitrates: [ 2,       9,   39,  124,  188,   188,  376 ] },
        { frameRate: '25p', bitrates: [ 2,      10,   40,  129,  196,   196,  391 ] },
        { frameRate: '29p', bitrates: [ 3,      12,   48,  155,  235,   235,  469 ] },
        { frameRate: '30p', bitrates: [ 3,      12,   48,  155,  235,   235,  470 ] },
        { frameRate: '47p', bitrates: [ 4,      19,   77,  248,  375,   375,  751 ] },
        { frameRate: '48p', bitrates: [ 4,      19,   77,  248,  376,   376,  752 ] },
        { frameRate: '50p', bitrates: [ 5,      20,   80,  259,  391,   391,  783 ] },
        { frameRate: '59p', bitrates: [ 6,      24,   96,  310,  469,   469,  939 ] },
        { frameRate: '60p', bitrates: [ 6,      24,   96,  310,  470,   470,  940 ] },
      ],
    },
    {
      resolution: 'UHD',
      frameRates: [ //            1/16 LB   1/4 LB    LB    SQ    HQ    HQX   444
        { frameRate: '24p', bitrates: [ 9,      36,  144,  462,  699,   699, 1399 ] },
        { frameRate: '25p', bitrates: [ 9,      37,  150,  481,  729,   729, 1457 ] },
        { frameRate: '29p', bitrates: [ 11,     44,  179,  577,  873,   873, 1747 ] },
        { frameRate: '30p', bitrates: [ 11,     45,  180,  578,  874,   874, 1749 ] },
        { frameRate: '47p', bitrates: [ 18,     71,  287,  NA,    NA,    NA,   NA ] },
        { frameRate: '48p', bitrates: [ 18,     71,  287,  NA,    NA,    NA,   NA ] },
        { frameRate: '50p', bitrates: [ 18,     74,  299,  963,  1457, 1457, 2914 ] },
        { frameRate: '59p', bitrates: [ 22,     74,  359,  1154, 1747, 1747, 3494 ] },
        { frameRate: '60p', bitrates: [ 22,     74,  359,  1155, 1749, 1749, 3497 ] },
      ],
    },
    {
      resolution: '4K',
      frameRates: [ //            1/16 LB   1/4 LB    LB    SQ    HQ    HQX   444
        { frameRate: '24p', bitrates: [ 9,      38,  153,  493,  746,   746, 1492 ] },
        { frameRate: '25p', bitrates: [ 10,     40,  160,  513,  777,   777, 1554 ] },
        { frameRate: '29p', bitrates: [ 12,     47,  191,  616,  932,   932, 1863 ] },
        { frameRate: '30p', bitrates: [ 12,     48,  192,  616,  933,   933, 1865 ] },
        { frameRate: '47p', bitrates: [ 19,     76,  306,  985, 1491,  1491, 2981 ] },
        { frameRate: '48p', bitrates: [ 19,     76,  306,  986, 1492,  1492, 2984 ] },
        { frameRate: '50p', bitrates: [ 20,     79,  319, 1027, 1554,  1554, 3109 ] },
        { frameRate: '59p', bitrates: [ 24,     95,  383, 1231, 1863,  1863, 3727 ] },
        { frameRate: '60p', bitrates: [ 24,     95,  383, 1232, 1865,  1865, 3730 ] },
      ],
    }
  ];

  const frameRateMap : Record<string, Fraction> = {
     '24p': { numerator: 24, denominator: 1 },
     '25p': { numerator: 25, denominator: 1 },
     '29p': { numerator: 30000, denominator: 1001 },
     '30p': { numerator: 30, denominator: 1 },
     '47p': { numerator: 48000, denominator: 1001 },
     '48p': { numerator: 48, denominator: 1 },
     '50p': { numerator: 50, denominator: 1 },
     '59p': { numerator: 60000, denominator: 1001 },
     '60p': { numerator: 60, denominator: 1 },
  };

  const resolutionMap : Record<string, Resolution> = {
    '2K': { width: 2048, height: 1080 },
    'UHD': { width: 3840, height: 2160 },
    '4K': { width: 4096, height: 2160 },
  };

  const profileNames = [ '1/16 LB', '1/4 LB', 'LB', 'SQ', 'HQ', 'HQX', '444'];

  const ffmpegProfiles : (DNxHRProfile | null)[] =
    [ null, null, 'lb', 'sq', 'hq', 'hqx', '444'];

  const pixelFormats : PixelFormat[] = [
    'yuv422p',
    'yuv422p',
    'yuv422p',
    'yuv422p',
    'yuv422p',
    'yuv422p10le',
    'yuv444p10le',
  ];

  const profiles : FilterableOutputProfile[] = [];
  for (const row of dnxhrTable) {
    const resolutionLookup = resolutionMap[row.resolution];
    invariant(resolutionLookup !== undefined, 'Missing resolution in map');
    const resolution: Resolution = resolutionLookup;
    for (const frameRateRow of row.frameRates) {
      const frameRateLookup = frameRateMap[frameRateRow.frameRate];
      invariant(frameRateLookup !== undefined, 'Missing frame rate in map');
      const frameRate: Fraction = frameRateLookup;
      for (let i = 0; i < profileNames.length; i++) {
        const bitrate = safeArrayAccess(frameRateRow.bitrates, i);
        if (bitrate === NA) {
          continue;
        }
        const profileName = profileNames[i];
        const ffmpegProfile = ffmpegProfiles[i];
        if (ffmpegProfile === null || ffmpegProfile === undefined) {
          continue;
        }
        const pixelFormat : PixelFormat = safeArrayAccess(pixelFormats, i);

        for (const audioChannels of [2, 4, 8, 16]) {
          const profile : FilterableOutputProfile = {
            type: 'broadcast',
            brandName: 'DNxHR',
            variant: profileName,
            container: {
              type: 'single_file',
              format: { name: 'mxf' },
            },
            video: {
              codec: {
                codec: 'dnxhr',
                profile: ffmpegProfile,
              },
              resolution: {
                width: resolution.width,
                height: resolution.height,
                sar: FRAC_ONE,
              },
              frameRate,
              pixelFormat: pixelFormat,
              fieldOrder: 'progressive',
              colorSpace: {
                matrix: {
                  type: 'BT.709',
                  range: 'limited',
                },
                primaries: 'BT.709',
                transfer: 'BT.709',
              },
            },
            audio: {
              codec: {
                codec: 'pcm',
                bitDepth: 24,
              },
              sampleRate: 48000,
              audioChannelsPerStream: 1,
              channelLimits: {
                min: audioChannels,
                max: audioChannels,
                defaultMix: false,
              },
            }
          };
          profiles.push(profile);
        }
      }
    }
  }
  return profiles;
}

function generateDnxhdProfiles(): FilterableOutputProfile[] {
  /**
   *  This table has been generated by combining the result from two different sources:
   *
   *    1.  Wikipedia has a list of valid DNxHD profiles: https://en.wikipedia.org/wiki/List_of_Avid_DNxHD_resolutions
   *    2.  Ffmpeg will spit out a list of all the supported video parameters for DNxHD by running:
   *
   *      ffmpeg -y -f lavfi -i testsrc -pix_fmt yuv422p -c:v dnxhd -f mxf /dev/null
   *
   *  We need to use the list reported by ffmpeg since using any other parameters will cause ffmpeg to fail, but the
   *  ffmpeg list does not provide the correct frame rate to use for each profile. Since the encoder just sees a sequence
   *  of images and does not consider time stamps at all there is not requirement in the actual encoder to match a specific
   *  frame rate. However, using the wrong frame rate with the wrong profile will cause non-standard MXF files which may not
   *  work with all software and the bitrate might be completely wrong. For instance if you encode "Avid DNxHD 185" at 1080p50
   *  instead of 1080p25 then you'd end up with double the bitrate that the "Avid DNxHD 185" profile actually should have.
   *
   *  Because of this I try to use the Wikipedia table as a baseline (since that have all the required information) and try
   *  to match this to one of the profiles outputted by ffmpeg. Some of the profiles listed in wikipedia I can't find a ffmpeg
   *  profiles for and for some ffmpeg profiles I can't find the profile in wikipedia. In both these cases we ignore the
   *  profile meaning that our profile list is incomplete, but in a different way depending on which source you reference.
   *
   *  Other notes:
   *    - Some of the ffmpeg profiles are available but currently only experimentally. We disable these profiles as well
   *    - There seems to currently be a ffmpeg bug that prevents users from selecting the 444 DNxHD profiles as there is
   *      a check during initialization that the dnxhr_444 profile is selected if the incoming pixel format is 444, but
   *      using this profile will make DNxHR nor DNxHD. Because of this we disable all the 444 variants as well.
   */
  const tables : Record<string, Record<string, [string, number, number, PixelFormat, number, boolean][]>> = {
    '1080' : {
      p60 : [
        ['440x', 1920, 1080, 'yuv422p10le', 440, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 440Mbps; pixel format: yuv422p10
        ['440',  1920, 1080,     'yuv422p', 440, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 440Mbps; pixel format: yuv422p
        ['290',  1920, 1080,     'yuv422p', 290, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 290Mbps; pixel format: yuv422p
        ['90',   1920, 1080,     'yuv422p',  90, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 90Mbps; pixel format: yuv422p
      ],
      p59 : [
        ['440x', 1920, 1080, 'yuv422p10le', 440, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 440Mbps; pixel format: yuv422p10
        ['440',  1920, 1080,     'yuv422p', 440, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 440Mbps; pixel format: yuv422p
        ['290',  1920, 1080,     'yuv422p', 290, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 290Mbps; pixel format: yuv422p
        ['90',   1920, 1080,     'yuv422p',  90, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 90Mbps; pixel format: yuv422p
      ],
      p50 : [
        ['365x', 1920, 1080, 'yuv422p10le', 365, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 365Mbps; pixel format: yuv422p10
        ['365',  1920, 1080,     'yuv422p', 365, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 365Mbps; pixel format: yuv422p
        ['240',  1920, 1080,     'yuv422p', 240, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 240Mbps; pixel format: yuv422p
        ['75',   1920, 1080,     'yuv422p',  75, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 75Mbps; pixel format: yuv422p
      ],
      i59 : [
        ['220x', 1920, 1080, 'yuv422p10le', 220, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080i; bitrate: 220Mbps; pixel format: yuv422p10
        ['220',  1920, 1080,     'yuv422p', 220, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080i; bitrate: 220Mbps; pixel format: yuv422p
        ['145',  1921, 1080,     'yuv422p', 145, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080i; bitrate: 145Mbps; pixel format: yuv422p
        ['100',  1440, 1080,     'yuv422p', 100, false], // [dnxhd @ 0x561d74cac380] Frame size: 1440x1080i; bitrate: 100Mbps; pixel format: yuv422p (experimental)
      ],
      p29 : [
        ['440x', 1920, 1080, 'yuv444p10le', 440, false], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 440Mbps; pixel format: yuv444p10, gbrp10
        ['220x', 1920, 1080, 'yuv422p10le', 220, false], // Unmatched?
        ['220',  1920, 1080,     'yuv422p', 220, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 220Mbps; pixel format: yuv422p
        ['145',  1920, 1080,     'yuv422p', 145, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 145Mbps; pixel format: yuv422p
        ['100',  1440, 1080,     'yuv422p', 100, true], // [dnxhd @ 0x561d74cac380] Frame size: 1440x1080p; bitrate: 100Mbps; pixel format: yuv422p
        ['45',   1920, 1080,     'yuv422p',  45, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 45Mbps; pixel format: yuv422p
      ],
      i50 : [
        ['185x', 1920, 1080, 'yuv422p10le', 185, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080i; bitrate: 185Mbps; pixel format: yuv422p10
        ['185',  1920, 1080,     'yuv422p', 185, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080i; bitrate: 185Mbps; pixel format: yuv422p
        ['120',  1920, 1080,     'yuv422p', 120, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080i; bitrate: 120Mbps; pixel format: yuv422p
        ['85',   1920, 1080,     'yuv422p',  85, false], // No matches??
      ],
      p25 : [
        ['365x', 1920, 1080, 'yuv444p10le', 365, false], // No matches??
        ['185x', 1920, 1080, 'yuv422p10le', 185, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 185Mbps; pixel format: yuv422p10
        ['185',  1920, 1080,     'yuv422p', 185, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 185Mbps; pixel format: yuv422p
        ['120',  1920, 1080,     'yuv422p', 120, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 120Mbps; pixel format: yuv422p
        ['85',   1440, 1080,     'yuv422p',  84, true], // [dnxhd @ 0x561d74cac380] Frame size: 1440x1080p; bitrate: 84Mbps; pixel format: yuv422p
        ['36',   1920, 1080,     'yuv422p',  36, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 36Mbps; pixel format: yuv422p
      ],
      p24 : [
        ['350x', 1920, 1080, 'yuv444p10le', 350, false], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 350Mbps; pixel format: yuv444p10, gbrp10
        ['175x', 1920, 1080, 'yuv422p10le', 175, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 175Mbps; pixel format: yuv422p10
        ['175',  1920, 1080,     'yuv422p', 175, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 175Mbps; pixel format: yuv422p
        ['115',  1920, 1080,     'yuv422p', 115, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 115Mbps; pixel format: yuv422p
        ['80',   1440, 1080,     'yuv422p',  80, false], // No matches?
        ['36',   1920, 1080,     'yuv422p',  36, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 36Mbps; pixel format: yuv422p
      ],
      p23 : [
        ['350x', 1920, 1080, 'yuv444p10le', 350, false], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 350Mbps; pixel format: yuv444p10, gbrp10
        ['175x', 1920, 1080, 'yuv422p10le', 175, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 175Mbps; pixel format: yuv422p10
        ['175',  1920, 1080,     'yuv422p', 175, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 175Mbps; pixel format: yuv422p
        ['115',  1920, 1080,     'yuv422p', 115, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 115Mbps; pixel format: yuv422p
        ['80',   1440, 1080,     'yuv422p',  80, false], // No matches??
        ['36',   1920, 1080,     'yuv422p',  36, true], // [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 36Mbps; pixel format: yuv422p
      ],
    },
    '720' : {
      p59: [
        ['220x', 1280, 720, 'yuv422p10le', 220, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 220Mbps; pixel format: yuv422p10
        ['220',  1280, 720,     'yuv422p', 220, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 220Mbps; pixel format: yuv422p
        ['145',  1280, 720,     'yuv422p', 145, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 145Mbps; pixel format: yuv422p
        ['100',   960, 720,     'yuv422p', 100, false], // No matches??
      ],
      p50 : [
        ['175x', 1280, 720, 'yuv422p10le', 175, false], // No matches??
        ['175',  1280, 720,     'yuv422p', 175, false], // No macthes??
        ['115',  1280, 720,     'yuv422p', 115, false], // No matches??
        ['85',    960, 720,     'yuv422p',  85, false], // No matches??
      ],
      p29 : [
        ['110x', 1280, 720, 'yuv422p10le', 110, false], // No matches??
        ['110',  1280, 720,     'yuv422p', 110, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 110Mbps; pixel format: yuv422p
        ['75',   1280, 720,     'yuv422p',  75, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 75Mbps; pixel format: yuv422p
        ['50',    960, 720,     'yuv422p',  51, false], // No matches??
      ],
      p25 : [
        ['90x', 1280, 720, 'yuv422p10le', 90, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 90Mbps; pixel format: yuv422p10
        ['90',  1280, 720,     'yuv422p', 90, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 90Mbps; pixel format: yuv422p
        ['60',  1280, 720,     'yuv422p', 60, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 60Mbps; pixel format: yuv422p
        ['45',   960, 720,     'yuv422p', 42, true], // [dnxhd @ 0x561d74cac380] Frame size: 960x720p; bitrate: 42Mbps; pixel format: yuv422p
      ],
      p23 : [
        ['90x', 1280, 720, 'yuv422p10le', 90, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 90Mbps; pixel format: yuv422p10
        ['90',  1280, 720,     'yuv422p', 90, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 90Mbps; pixel format: yuv422p
        ['60',  1280, 720,     'yuv422p', 60, true], // [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 60Mbps; pixel format: yuv422p
        ['45',   960, 720,     'yuv422p', 42, true], // [dnxhd @ 0x561d74cac380] Frame size: 960x720p; bitrate: 42Mbps; pixel format: yuv422p
      ]
    }
  };

  /*
   *  The following profiles where outputted from ffmpeg, but I can't seem to find any match for them in the
   *  wikipedia table:
   *
   *  [dnxhd @ 0x561d74cac380] Frame size: 1440x1080i; bitrate: 80Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1440x1080p; bitrate: 63Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 180Mbps; pixel format: yuv422p10
   *  [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 180Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 960x720p; bitrate: 75Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1440x1080i; bitrate: 90Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1440x1080i; bitrate: 145Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1440x1080i; bitrate: 120Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1280x720p; bitrate: 120Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1440x1080i; bitrate: 80Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1440x1080p; bitrate: 110Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1440x1080i; bitrate: 110Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 960x720p; bitrate: 60Mbps; pixel format: yuv422p
   *  [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 390Mbps; pixel format: yuv444p10, gbrp10
   *  [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 730Mbps; pixel format: yuv444p10, gbrp10
   *  [dnxhd @ 0x561d74cac380] Frame size: 1920x1080p; bitrate: 880Mbps; pixel format: yuv444p10, gbrp10
   *  [dnxhd @ 0x561d74cac380] Frame size: 960x720p; bitrate: 115Mbps; pixel format: yuv422p
   **/

  const frameRates : Record<string, { frameRate: Fraction, fieldOrder: FieldOrder }> = {
    p60 : { frameRate: { numerator: 60, denominator: 1 }, fieldOrder: 'progressive' },
    p59 : { frameRate: { numerator: 60000, denominator: 1001 }, fieldOrder: 'progressive' },
    p50 : { frameRate: { numerator: 50, denominator: 1 }, fieldOrder: 'progressive' },
    i59 : { frameRate: { numerator: 30000, denominator: 1001 }, fieldOrder: 'top_field_first' },
    p29 : { frameRate: { numerator: 30000, denominator: 1001 }, fieldOrder: 'progressive' },
    i50 : { frameRate: { numerator: 25, denominator: 1 }, fieldOrder: 'top_field_first' },
    p25 : { frameRate: { numerator: 25, denominator: 1 }, fieldOrder: 'progressive' },
    p24 : { frameRate: { numerator: 24, denominator: 1 }, fieldOrder: 'progressive' },
    p23 : { frameRate: { numerator: 24000, denominator: 1001 }, fieldOrder: 'progressive' },
  };

  const audioChannelCounts : number[] = [2, 4, 8, 16];
  const profiles : FilterableOutputProfile[] = [];
  for (const h in tables) {
    const tableH = tables[h];
    invariant(tableH !== undefined, `Missing table for resolution ${h}`);
    for (const mode in tableH) {
      const rows = tableH[mode];
      invariant(rows !== undefined, `Missing rows for mode ${mode}`);
      for (const row of rows) {
        for (const audioChannelCount of audioChannelCounts) {
          const [profileName, width, height, pixelFormat, bitrate, supportedByFfmpeg] = row;
          if (!supportedByFfmpeg) {
            continue;
          }

          const anamorphic = (height * 4 === width * 3);
          const sar = anamorphic ? { numerator: 4, denominator: 3 } : FRAC_ONE;
          const frameRateLookup = frameRates[mode];
          invariant(frameRateLookup !== undefined, `Missing frame rate for mode ${mode}`);
          const { frameRate, fieldOrder } = frameRateLookup;
          const profile : FilterableOutputProfile = {
            type: 'broadcast',
            brandName: 'DNxHD',
            variant: profileName,
            container: {
              type: 'single_file',
              format: { name: 'mxf' },
            },
            video: {
              codec: {
                codec: 'dnxhd',
                rateControl: {
                  type: 'cbr',
                  bitrate: bitrate * 1000000,
                },
              },
              pixelFormat,
              resolution: { width, height, sar },
              frameRate,
              fieldOrder,
              colorSpace: {
                matrix: {
                  type: 'BT.709',
                  range: 'limited',
                },
                primaries: 'BT.709',
                transfer: 'BT.709',
              },
            },
            audio: {
              codec: {
                codec: 'pcm',
                bitDepth: 24,
              },
              sampleRate: 48000,
              audioChannelsPerStream: 1,
              channelLimits: {
                min: audioChannelCount,
                max: audioChannelCount,
                defaultMix: false,
              },
            }
          };
          profiles.push(profile);
        }
      }
    }
  }
  return profiles;
}

